// English translations for Plan Summary feature
// Location: src/i18n/locales/en/planSummary.ts

export default {
  // Page Header
  header: {
    title: 'Plan Summary',
    dateRange: '{{startDate}} - {{endDate}}',
    actions: {
      downloadPdf: 'PDF',
      share: 'Share',
      saveJourney: "Save your journey"
    },
  },

  sections: {
    journey: "The Journey",
    weeklyPerformance: "Weekly Performance",
    dailyHighlights: "Daily Highlights",
    aiAnalysis: "AI Analysis",
    trophyRoom: "Trophy Room"
  },

  // Quick Stats (Header Cards)
  quickStats: {
    weightChange: 'Weight Change',
    completion: 'Completion',
    adherence: 'Adherence',
    goalStatus: 'Goal Status',
    achieved: 'Achieved',
    inProgress: 'In Progress',
  },

  // Summary Stats
  stats: {
    startWeight: 'Start Weight',
    endWeight: 'End Weight',
    daysCompleted: 'Days Completed',
    mealsLogged: 'Meals Logged',
    avgCalories: 'Avg Calories/Day',
    avgProtein: 'Avg Protein/Day',
    avgCarbs: 'Avg Carbs/Day',
    avgFat: 'Avg Fat/Day',
  },

  // Weight Journey Chart
  weightJourney: {
    title: 'Weight Journey',
    subtitle: "Track your progress over time",
    legend: {
      progress: 'Your Progress',
      goal: 'Goal',
    },
    tooltip: {
      weight: 'Weight',
    },
    summary: {
      start: 'Start',
      end: 'End',
      change: 'Change',
    },
    loading: 'Loading chart...',
  },

  // Weekly Breakdown
  weeklyBreakdown: {
    title: 'Weekly Breakdown',
    subtitle: "Week-by-week consistency",
    bestWeek: 'Best: Week {{week}} ({{adherence}}%)',
    week: 'Week {{number}}',
    daysCompleted: '{{count}} days completed',
    adherence: 'Adherence',
    performance: {
      excellent: 'Excellent consistency!',
      good: 'Good progress',
      improvement: 'Room for improvement',
    },
    summary: {
      avgAdherence: 'Avg Adherence',
      totalWeightChange: 'Total Weight Change',
      weeksCompleted: 'Weeks Completed',
    },
  },

  // Best/Worst Days
  performanceHighlights: {
    title: 'Performance Highlights',
    subtitle: "Daily peaks and valleys",
    bestDays: {
      title: 'Best Days',
      day: 'Day {{number}}',
      empty: 'No perfect days recorded',
    },
    worstDays: {
      title: 'Challenging Days',
      day: 'Day {{number}}',
      empty: 'No challenging days - great work!',
    },
    insights: {
      moreBest: 'You had more perfect days than challenging ones! This shows strong consistency and dedication to your plan.',
      moreWorst: 'Use challenging days as learning opportunities. Identify patterns and prepare strategies for similar situations in the future.',
      consistent: 'Your performance was remarkably consistent throughout the plan. Keep up this excellent discipline!',
    },
  },

  // Insights Card
  insights: {
    title: 'AI Insights',
        subtitle: "Intelligent observations based on your data",
    empty: 'No specific insights generated for this plan',
    generalTips: {
      title: 'General Tips',
      consistency: 'Consistency is more important than perfection',
      timing: 'Track your weight at the same time each day',
      improvements: 'Small daily improvements lead to big results',
    },
  },

  // Achievements
  achievements: {
    title: 'Achievements Unlocked',
    subtitle: "Milestones unlocked during this plan",
    unlockedLabel: "Unlocked",
    badge: 'Badge',
    badges: 'Badges',
    celebration: {
      title: 'Amazing Progress! 🎉',
      message: 'You unlocked {{count}} achievement during this plan. Each one represents your dedication and hard work!',
      messagePlural: 'You unlocked {{count}} achievements during this plan. Each one represents your dedication and hard work!',
    },
  },

  // Next Steps
  nextSteps: {
    title: "What's Next?",
    message: {
      goalAchieved: {
        title: 'Congratulations on Achieving Your Goal!',
        description: "You've reached your target! Now it's time to either maintain your results or set a new challenge. The choice is yours!",
      },
      goodAdherence: {
        title: 'Keep Going!',
        description: "You showed great consistency ({{adherence}}% adherence). With a few adjustments, you'll reach your goal in the next plan!",
      },
      needsImprovement: {
        title: 'Keep Going!',
        description: "You completed the plan with {{adherence}}% adherence. Let's work together to make the next one even better!",
      },
    },
    actions: {
      newPlan: {
        title: 'Start a New Plan',
        descriptionAchieved: 'Set a new goal and keep the momentum going!',
        descriptionContinue: 'Continue your journey with a fresh 30-day plan',
      },
      maintenance: {
        title: 'Maintenance Mode',
        descriptionAchieved: 'Keep your results with a maintenance plan',
        descriptionContinue: 'Maintain your current weight while building habits',
      },
      adjust: {
        title: 'Adjust Your Approach',
        descriptionLow: "Let's fine-tune your plan based on what worked best",
        descriptionHigh: 'Build on your success with optimized strategies',
      },
      getStarted: 'Get Started',
    },
    quickActions: {
      title: 'Quick Actions',
      dashboard: 'Back to Dashboard',
      progress: 'View All Progress',
      askBot: 'Ask NutriBot',
    },
  },

  // Loading States
  loading: {
    title: 'Loading Summary...',
    generating: 'Generating Your Summary...',
    description: 'Please wait...',
    generatingDescription: 'This will take about 30 seconds',
  },

  // Error States
  error: {
    title: 'Failed to Load Summary',
    tryAgain: 'Try Again',
    backToDashboard: 'Back to Dashboard',
  },

  // Plan Completion Modal
  completionModal: {
    title: 'Plan Completed!',
    subtitle: 'You finished your {{days}}-day journey!',
    stats: {
      daysCompleted: 'Days Completed',
      weightChange: 'Weight Change',
    },
    goalAchieved: {
      title: 'Goal Achieved! 🎯',
      description: 'You reached your target weight!',
    },
    actions: {
      viewSummary: 'View Your Plan Summary',
      generateSummary: 'Generate My Summary',
      checkStatus: 'Check Status',
      later: "I'll check it later",
    },
    status: {
      checking: 'Checking...',
      generating: 'Generating Summary...',
    },
  },

  // Plan Summary Banner
  banner: {
    lastDay: {
      complete: '🏆 Last Day Complete!',
      completeDescription: 'Your plan summary is being generated...',
      incomplete: "🎯 You're on Day {{current}}/{{total}}!",
      incompleteDescription: 'Complete your last meals to finish your plan',
    },
    planComplete: {
      title: '🎉 Plan Complete!',
      description: 'Your plan summary is ready to view',
    },
    actions: {
      viewSummary: 'View Summary',
      checkStatus: 'Check Status',
    },
  },

  // Metadata
  metadata: {
    generatedOn: 'Generated {{date}} at {{time}}',
    generatedBy: 'Generated by: {{source}}',
    sources: {
      user: 'User Request',
      cron: 'Automatic System',
    },
  },

  // Alerts
  alerts: {
    linkCopied: 'Link copied to clipboard!',
    pdfComingSoon: 'PDF download coming soon!',
    summaryGenerationFailed: 'Failed to generate summary. Please try again.',

    generatingPDF: 'Generating PDF...',
    pdfDownloaded: 'PDF downloaded successfully!',
    pdfGenerationFailed: 'Failed to generate PDF. Please try again.',
  },

  prompts: {
    newPlan: "I'd like to create a new plan.",
    maintenance: "I'd like a new plan focused on maintenance.",
    adjustPlan: "I'd like to adjust my current plan.",
    askAboutSummary: "What do you think of my plan summary?"
  },
};